// IM-UX-168..174 (IM-ADR-020): peças compartilhadas da administração da plataforma — formatação, campos de código,
// QR do cadastro e exibição única de códigos. Nenhum segredo é guardado em estado global, armazenamento local ou log.
import { useState, type FormEvent, type ReactNode } from 'react';
import qrcode from 'qrcode-generator';
import { ApiError } from '../api/client';
import { friendlyError } from '../lib/errors';

export const PLATFORM_BASE = '/admin/plataforma';
export const dt = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
export const day = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');
export const PURPOSE: Record<string, [string, string]> = { REAL: ['Real', 'mint'], TECNICO: ['Técnica', 'gray'], DEMONSTRACAO: ['Demonstração', 'amber'] };
export const OPERATIONAL: Record<string, [string, string]> = { ACTIVE: ['Ativa', 'mint'], SUSPENDED: ['Suspensa', 'critical'] };
export const SUBSCRIPTION: Record<string, string> = { TRIAL: 'Em teste', ACTIVE: 'Ativa', SUSPENDED: 'Suspensa', CANCELED: 'Cancelada', SEM_REGISTRO: 'Sem registro' };
export const Tag = ({ map, value }: { map: Record<string, [string, string]>; value: string }) => { const [l, t] = map[value] ?? [value, 'gray']; return <span className={`tag ${t}`}>{l}</span>; };

/** Troca de contexto: o servidor emite outra sessão; a página recarrega do zero no destino. */
export const reloadTo = (path: string) => window.location.assign(path);

export const Kpis = ({ items }: { items: Array<[string, number | string]> }) => (
  <dl className="kpi-row pa-kpis">{items.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
);

/** Mensagem de erro da API para formulários administrativos (409/403 já vêm com texto do servidor). */
export const apiMessage = (err: unknown, fallback: string) =>
  err instanceof ApiError && err.status === 429 ? 'Muitas tentativas. Aguarde um minuto.'
    : err instanceof ApiError && [400, 403, 409].includes(err.status) && err.message ? err.message : friendlyError(err, fallback);

export function CodeField({ id, value, onChange, label = 'Código do aplicativo autenticador', hint }: { id: string; value: string; onChange: (v: string) => void; label?: string; hint?: string }) {
  return (
    <div className="field"><label htmlFor={id}>{label}</label>
      <input id={id} className="pa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={value} aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))} />
      {hint && <small id={`${id}-hint`} className="field-hint">{hint}</small>}
    </div>
  );
}

export function ReasonField({ id, value, onChange, label = 'Motivo' }: { id: string; value: string; onChange: (v: string) => void; label?: string }) {
  return <div className="field"><label htmlFor={id}>{label}</label><textarea id={id} rows={2} maxLength={500} value={value} onChange={(e) => onChange(e.target.value)} aria-required="true" /></div>;
}

export const STEP_UP_HINT = 'Use um código novo: o mesmo código não vale duas vezes.';

/**
 * Formulário padrão das operações sensíveis: motivo + código novo do aplicativo + ação. Validação local mínima; a regra
 * vale no servidor.
 */
export function SensitiveForm({ idPrefix, children, submitLabel, busyLabel, danger, onCancel, onSubmit, extraValid = true }: {
  idPrefix: string; children?: ReactNode; submitLabel: string; busyLabel: string; danger?: boolean; onCancel: () => void;
  onSubmit: (v: { reason: string; code: string }) => Promise<void>; extraValid?: boolean;
}) {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 5) { setError('Explique o motivo (mínimo de 5 caracteres).'); return; }
    if (!/^\d{6}$/.test(code)) { setError('Informe os 6 dígitos do aplicativo autenticador.'); return; }
    if (!extraValid) { setError('Confira a confirmação antes de continuar.'); return; }
    setBusy(true); setError(null);
    try { await onSubmit({ reason: reason.trim(), code }); } catch (err) { setError(apiMessage(err, 'Não foi possível concluir agora.')); setCode(''); setBusy(false); }
  };
  return (
    <form className="form pa-form" onSubmit={submit} noValidate>
      {children}
      {error && <div className="form-error" role="alert">{error}</div>}
      <ReasonField id={`${idPrefix}-reason`} value={reason} onChange={setReason} />
      <CodeField id={`${idPrefix}-code`} value={code} onChange={setCode} hint={STEP_UP_HINT} />
      <div className="form-actions"><button type="button" className="secondary" onClick={onCancel}>Cancelar</button>
        <button className={danger ? 'cta danger' : 'primary'} type="submit" disabled={busy}>{busy ? busyLabel : submitLabel}</button></div>
    </form>
  );
}

/** QR desenhado em SVG (sem imagem externa, sem HTML injetado). */
export function QrCode({ value, label }: { value: string; label: string }) {
  const qr = qrcode(0, 'M');
  qr.addData(value);
  qr.make();
  const n = qr.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
  return (
    <svg className="pa-qr" viewBox={`0 0 ${n + 8} ${n + 8}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width={n + 8} height={n + 8} fill="#fff" /><path d={d} fill="#06211f" />
    </svg>
  );
}

const copy = (text: string) => navigator.clipboard?.writeText(text).catch(() => undefined);

/** Código de cadastro (uma vez, para quem emitiu): entregar fora de banda. */
export function TicketReveal({ ticket, expiresAt, who, onDone }: { ticket: string; expiresAt: string; who: string; onDone: () => void }) {
  const [ack, setAck] = useState(false);
  return (
    <div className="pa-reveal">
      <p>Entregue este código a <b>{who}</b> por um canal seguro (pessoalmente ou por telefone). Ele aparece só agora, vale até {dt(expiresAt)} e só funciona na conta dessa pessoa, com a senha dela.</p>
      <div className="pa-secret"><code aria-label="Código de cadastro">{ticket}</code><button type="button" className="link-btn" onClick={() => copy(ticket)}>Copiar</button></div>
      <label className="pa-toggle"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Anotei o código para entregar</label>
      <div className="form-actions"><button type="button" className="primary" disabled={!ack} onClick={onDone}>Concluir</button></div>
    </div>
  );
}

/** Códigos de recuperação (uma vez, para o titular). */
export function RecoveryCodesReveal({ codes, onDone, doneLabel = 'Concluir' }: { codes: string[]; onDone: () => void; doneLabel?: string }) {
  const [ack, setAck] = useState(false);
  return (
    <div className="pa-reveal">
      <p>Guarde estes códigos de recuperação em local seguro. Cada um vale <b>uma vez</b> para entrar se você perder o aparelho; eles não aparecem de novo e os anteriores deixaram de valer.</p>
      <ol className="pa-codes" aria-label="Códigos de recuperação">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ol>
      <div className="pa-reveal-actions"><button type="button" className="link-btn" onClick={() => copy(codes.join('\n'))}>Copiar todos</button></div>
      <label className="pa-toggle"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Guardei os códigos</label>
      <div className="form-actions"><button type="button" className="primary" disabled={!ack} onClick={onDone}>{doneLabel}</button></div>
    </div>
  );
}

/** Configuração do aplicativo autenticador (QR + chave manual) e confirmação com o primeiro código. */
export function AuthenticatorSetup({ start, onConfirm, onCancel }: { start: { otpauthUri: string; secret: string; expiresAt: string }; onConfirm: (code: string) => Promise<void>; onCancel?: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError('Informe os 6 dígitos do aplicativo autenticador.'); return; }
    setBusy(true); setError(null);
    try { await onConfirm(code); } catch (err) { setError(apiMessage(err, 'Não foi possível confirmar agora.')); setCode(''); setBusy(false); }
  };
  return (
    <div className="pa-setup">
      <div className="pa-setup-qr"><QrCode value={start.otpauthUri} label="QR code para o aplicativo autenticador" /></div>
      <div className="pa-setup-steps">
        <ol className="pa-steps"><li>Abra o aplicativo autenticador e leia o QR code.</li><li>Sem câmera? Digite a chave abaixo.</li><li>Informe o código de 6 dígitos que aparecer.</li></ol>
        <div className="pa-secret"><code aria-label="Chave para digitação manual">{start.secret.match(/.{1,4}/g)?.join(' ')}</code><button type="button" className="link-btn" onClick={() => copy(start.secret)}>Copiar</button></div>
        <form className="form" onSubmit={submit} noValidate>
          {error && <div className="form-error" role="alert">{error}</div>}
          <CodeField id="pa-setup-code" value={code} onChange={setCode} label="Código do aplicativo" hint={`Este cadastro vale até ${dt(start.expiresAt)}.`} />
          <div className="form-actions">{onCancel && <button type="button" className="secondary" onClick={onCancel}>Cancelar</button>}
            <button className="primary" type="submit" disabled={busy}>{busy ? 'Confirmando…' : 'Confirmar aplicativo'}</button></div>
        </form>
      </div>
    </div>
  );
}
